import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAutenticacao } from '../../context/autenticacao_contexto';
import { cores } from '../../theme';

import {
    Typography, Container, Box, CircularProgress, Alert, Paper,
    List, ListItem, ListItemText, Button, Divider, Chip, IconButton,
    Avatar, ListItemAvatar, Fade, useTheme, Grid, Card, CardMedia,
    ImageList, ImageListItem, Modal
} from '@mui/material';
import {
    ArrowBack, CheckCircle, HourglassEmpty, Assignment,
    Grading, Person, Star, Edit, RateReview, Article as ArticleIcon,
    Image as ImageIcon, ZoomIn as ZoomInIcon, Close as CloseIcon,
    CalendarMonth, Close
} from '@mui/icons-material';

const CasoDetalhePagina = () => {
    const { grupoId, casoId } = useParams();
    const { perfilUsuario } = useAutenticacao();
    const [dadosPagina, setDadosPagina] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [imagemAmpliada, setImagemAmpliada] = useState(null);
    const navegar = useNavigate();
    const theme = useTheme();

    useEffect(() => {
        const buscarDados = async () => {
            if (!grupoId || !casoId || !perfilUsuario) return;
            try {
                setCarregando(true);
                setErro('');
                const resposta = await api.get(`/api/grupos/${grupoId}/casos/${casoId}/detalhes`);
                setDadosPagina(resposta.data);
            } catch (err) {
                setErro('Não foi possível carregar os detalhes do caso. Verifique suas permissões.');
                console.error(err);
            } finally {
                setCarregando(false);
            }
        };
        buscarDados();
    }, [grupoId, casoId, perfilUsuario]);

    const notaFinal = useMemo(() => {
        if (dadosPagina?.statusAvaliacao === 'CONCLUIDA' && dadosPagina.minhaAvaliacao) {
            return dadosPagina.minhaAvaliacao.reduce((total, item) => {
                return total + parseFloat(item.nota_ponderada);
            }, 0);
        }
        return 0;
    }, [dadosPagina]);

    const ampliarImagem = (imagem) => {
        setImagemAmpliada(imagem);
    };

    const fecharImagemAmpliada = () => {
        setImagemAmpliada(null);
    };

    if (carregando) {
        return (
            <Container maxWidth="lg" sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: cores.primaria.principal }} />
            </Container>
        );
    }

    if (erro) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert
                    severity="error"
                    sx={{
                        borderRadius: 2,
                        backgroundColor: cores.erro.claro,
                        borderColor: cores.erro.medio,
                        color: cores.erro.escuro,
                        '& .MuiAlert-icon': {
                            alignItems: 'center'
                        }
                    }}
                >
                    {erro}
                </Alert>
            </Container>
        );
    }

    if (!dadosPagina) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert
                    severity="info"
                    sx={{
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                            alignItems: 'center'
                        }
                    }}
                >
                    Dados não encontrados.
                </Alert>
            </Container>
        );
    }

    const { caso, imagens, turma } = dadosPagina;

    const renderizarConteudoPorPerfil = () => {
        if (!perfilUsuario) return null;

        if (perfilUsuario.cargo === 'PROFESSOR') {
            const { criteriosParaAvaliar, alunosDoGrupo } = dadosPagina;

            return (
                <>
                    {criteriosParaAvaliar && (
                        <Fade in={true} timeout={600}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 2, sm: 3 },
                                    my: 3,
                                    background: '#f8f9fa',
                                    borderRadius: 2,
                                    border: `1px solid ${cores.cinza.claro}`,
                                    boxShadow: cores.sombras.suave
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Avatar
                                        sx={{
                                            mr: 2,
                                            bgcolor: 'rgba(30, 58, 138, 0.1)',
                                            color: cores.primaria.principal
                                        }}
                                    >
                                        <Grading />
                                    </Avatar>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: cores.primaria.principal }}>
                                        Critérios a Serem Avaliados
                                    </Typography>
                                </Box>
                                <List>
                                    {criteriosParaAvaliar.map((criterio, index) => (
                                        <ListItem
                                            key={criterio.id}
                                            divider={index < criteriosParaAvaliar.length - 1}
                                            sx={{
                                                px: 2,
                                                py: 1.5,
                                                borderColor: 'rgba(0,0,0,0.08)'
                                            }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" sx={{ fontWeight: 600, color: cores.cinza.escuro }}>
                                                        {criterio.descricao}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                                                        <Chip
                                                            label={`Peso: ${criterio.peso}`}
                                                            size="small"
                                                            sx={{
                                                                fontWeight: 600,
                                                                bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.08)`,
                                                                color: cores.primaria.principal,
                                                                height: 24
                                                            }}
                                                        />
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        </Fade>
                    )}

                    {alunosDoGrupo && (
                        <Fade in={true} timeout={800}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 2, sm: 3 },
                                    my: 3,
                                    background: '#f8f9fa',
                                    borderRadius: 2,
                                    border: `1px solid ${cores.cinza.claro}`,
                                    boxShadow: cores.sombras.suave
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Avatar
                                        sx={{
                                            mr: 2,
                                            bgcolor: 'rgba(30, 58, 138, 0.1)',
                                            color: cores.primaria.principal
                                        }}
                                    >
                                        <Person />
                                    </Avatar>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: cores.primaria.principal }}>
                                        Alunos do Grupo para Avaliar
                                    </Typography>
                                </Box>
                                <List>
                                    {alunosDoGrupo.map((aluno, index) => (
                                        <React.Fragment key={aluno.id}>
                                            <ListItem sx={{
                                                px: 2,
                                                py: 1.5,
                                                display: 'flex',
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                alignItems: { xs: 'flex-start', sm: 'center' },
                                                gap: { xs: 2, sm: 0 }
                                            }}>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body1" sx={{ fontWeight: 600, color: cores.cinza.escuro }}>
                                                            {aluno.nome_completo}
                                                        </Typography>
                                                    }
                                                />
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    {aluno.foi_avaliado && aluno.nota_final && (
                                                        <Chip
                                                            label={`Nota: ${Number(aluno.nota_final).toFixed(1)}`}
                                                            size="small"
                                                            color={Number(aluno.nota_final) >= 70 ? "success" : "warning"}
                                                            sx={{
                                                                fontWeight: 600,
                                                                height: 24
                                                            }}
                                                        />
                                                    )}
                                                    <Button
                                                        variant={aluno.foi_avaliado ? "outlined" : "contained"}
                                                        size="small"
                                                        startIcon={aluno.foi_avaliado ? <Edit /> : <RateReview />}
                                                        component={RouterLink}
                                                        to={aluno.foi_avaliado
                                                            ? `/avaliacoes/editar/${aluno.avaliacao_id}`
                                                            : `/avaliar/turma/${turma.id}/grupo/${grupoId}/aluno/${aluno.id}/caso/${casoId}`}
                                                        sx={{
                                                            borderRadius: 2,
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            transition: 'all 0.2s ease',
                                                            alignSelf: { xs: 'flex-start', sm: 'center' },
                                                            px: 2,
                                                            py: 0.8,
                                                            boxShadow: aluno.foi_avaliado ? 'none' : cores.sombras.suave,
                                                            bgcolor: aluno.foi_avaliado ? 'transparent' : cores.primaria.principal,
                                                            borderColor: aluno.foi_avaliado ? cores.primaria.principal : 'transparent',
                                                            color: aluno.foi_avaliado ? cores.primaria.principal : 'white',
                                                            '&:hover': {
                                                                backgroundColor: aluno.foi_avaliado ? `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.08)` : cores.primaria.escuro,
                                                                borderColor: aluno.foi_avaliado ? cores.primaria.principal : 'transparent',
                                                                boxShadow: aluno.foi_avaliado ? 'none' : cores.sombras.medio,
                                                            }
                                                        }}
                                                    >
                                                        {aluno.foi_avaliado ? "Editar avaliação" : "Avaliar aluno"}
                                                    </Button>
                                                </Box>
                                            </ListItem>
                                            {index < alunosDoGrupo.length - 1 && <Divider sx={{ borderColor: 'rgba(0,0,0,0.08)' }} />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            </Paper>
                        </Fade>
                    )}
                </>
            );
        }

        if (perfilUsuario.cargo === 'ALUNO') {
            const { minhaAvaliacao, templateAvaliacao, statusAvaliacao, frequenciaSalva } = dadosPagina;

            if (!statusAvaliacao) return <CircularProgress sx={{ color: cores.primaria.principal }} />;

            const estaConcluida = statusAvaliacao === 'CONCLUIDA';

            return (
                <Fade in={true} timeout={600}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2, md: 3 },
                            my: 3,
                            background: '#f8f9fa',
                            borderRadius: 2,
                            border: `1px solid ${cores.cinza.claro}`,
                            boxShadow: cores.sombras.suave
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                justifyContent: 'space-between',
                                gap: 1,
                                mb: 2
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar
                                    sx={{
                                        mr: 2,
                                        bgcolor: 'rgba(30, 58, 138, 0.1)',
                                        color: cores.primaria.principal
                                    }}
                                >
                                    <Star />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: cores.primaria.principal }}>
                                    Meu Desempenho
                                </Typography>
                            </Box>
                            <Chip
                                icon={estaConcluida ? <CheckCircle /> : <HourglassEmpty />}
                                label={estaConcluida ? 'Avaliação Concluída' : 'Avaliação Pendente'}
                                color={estaConcluida ? 'success' : 'warning'}
                                sx={{
                                    alignSelf: { xs: 'flex-start', sm: 'center' },
                                    fontWeight: 600,
                                    borderRadius: 1
                                }}
                            />
                        </Box>

                        {estaConcluida ? (
                            <>
                                <Box
                                    sx={{
                                        my: { xs: 2, sm: 3 },
                                        p: { xs: 2, sm: 3 },
                                        borderRadius: 2,
                                        textAlign: 'center',
                                        background: 'linear-gradient(135deg, #e5f0ff 0%, #ffffff 100%)',
                                        border: `1px solid rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.15)`,
                                        boxShadow: cores.sombras.suave
                                    }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            color: cores.primaria.escuro,
                                            fontWeight: 600,
                                            mb: 1
                                        }}
                                    >
                                        Nota Final
                                    </Typography>
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            fontWeight: 'bold',
                                            color: cores.primaria.principal,
                                            fontSize: { xs: '2.5rem', sm: '3rem' },
                                            textShadow: `0 2px 4px rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)`
                                        }}
                                    >
                                        {notaFinal.toFixed(2)} / 100
                                    </Typography>
                                </Box>

                                {frequenciaSalva && (
                                    <Box
                                        sx={{
                                            mt: 3,
                                            p: { xs: 2, sm: 3 },
                                            borderRadius: 2,
                                            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                                            border: `1px solid ${cores.cinza.claro}`,
                                            boxShadow: cores.sombras.suave
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                color: cores.secundaria.principal,
                                                fontWeight: 600,
                                                mb: 3,
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <CalendarMonth sx={{ mr: 1 }} />
                                            Registro de Frequência
                                        </Typography>

                                        <Box sx={{
                                            maxWidth: '600px',
                                            mx: 'auto',
                                            mb: 2
                                        }}>
                                            {(() => {
                                                const totalAulas = 8;
                                                const presencasAula1 = frequenciaSalva.presencas.aula_1.filter(p => p).length;
                                                const presencasAula2 = frequenciaSalva.presencas.aula_2.filter(p => p).length;
                                                const aulasPresente = presencasAula1 + presencasAula2;
                                                const percentualFrequencia = (aulasPresente / totalAulas) * 100;

                                                return (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: 2,
                                                        flexDirection: { xs: 'column', sm: 'row' },
                                                        mb: 3,
                                                        p: 2,
                                                        borderRadius: 2,
                                                        bgcolor: 'rgba(0, 128, 0, 0.05)',
                                                        border: '1px solid rgba(0, 128, 0, 0.1)'
                                                    }}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1
                                                        }}>
                                                            <CheckCircle sx={{ color: cores.sucesso.principal }} />
                                                            <Typography
                                                                variant="body1"
                                                                sx={{ fontWeight: 600, color: cores.cinza.escuro }}
                                                            >
                                                                Frequência Total:
                                                            </Typography>
                                                        </Box>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                fontWeight: 700,
                                                                color: percentualFrequencia >= 75 ? cores.sucesso.principal : cores.aviso.principal
                                                            }}
                                                        >
                                                            {aulasPresente}/{totalAulas} aulas ({Math.round(percentualFrequencia)}%)
                                                        </Typography>
                                                    </Box>
                                                );
                                            })()}

                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                gap: 3,
                                                mb: 2
                                            }}>
                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        flex: 1,
                                                        borderRadius: 2,
                                                        border: `1px solid ${cores.cinza.claro}`,
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            bgcolor: cores.cinza.clarissimo,
                                                            p: 1.5,
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            borderBottom: `1px solid ${cores.cinza.claro}`
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <CalendarMonth sx={{ color: cores.secundaria.principal, fontSize: '1.2rem' }} />
                                                            <Typography
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: cores.cinza.escuro
                                                                }}
                                                            >
                                                                Aula 1
                                                            </Typography>
                                                        </Box>
                                                        <Chip
                                                            size="small"
                                                            label={`${frequenciaSalva.presencas.aula_1.filter(p => p).length}/4`}
                                                            color={frequenciaSalva.presencas.aula_1.filter(p => p).length >= 3 ? "success" : frequenciaSalva.presencas.aula_1.filter(p => p).length > 0 ? "warning" : "error"}
                                                            sx={{ height: 24, fontWeight: 600 }}
                                                        />
                                                    </Box>

                                                    <Box sx={{ p: 2 }}>
                                                        <Grid container spacing={1}>
                                                            {frequenciaSalva.presencas.aula_1.map((presente, aulaIndex) => (
                                                                <Grid item xs={6} key={aulaIndex}>
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            p: 1,
                                                                            borderRadius: 1,
                                                                            bgcolor: presente ? 'rgba(0, 200, 83, 0.08)' : 'transparent',
                                                                            border: presente ? '1px solid rgba(0, 200, 83, 0.2)' : '1px solid transparent'
                                                                        }}
                                                                    >
                                                                        {presente ?
                                                                            <CheckCircle fontSize="small" sx={{ color: cores.sucesso.principal, mr: 1 }} /> :
                                                                            <Close fontSize="small" sx={{ color: cores.erro.principal, mr: 1 }} />
                                                                        }
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontWeight: presente ? 600 : 400,
                                                                                color: presente ? cores.secundaria.principal : cores.cinza.escuro
                                                                            }}
                                                                        >
                                                                            Presença {aulaIndex + 1}
                                                                        </Typography>
                                                                    </Box>
                                                                </Grid>
                                                            ))}
                                                        </Grid>
                                                    </Box>
                                                </Paper>

                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        flex: 1,
                                                        borderRadius: 2,
                                                        border: `1px solid ${cores.cinza.claro}`,
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            bgcolor: cores.cinza.clarissimo,
                                                            p: 1.5,
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            borderBottom: `1px solid ${cores.cinza.claro}`
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <CalendarMonth sx={{ color: cores.secundaria.principal, fontSize: '1.2rem' }} />
                                                            <Typography
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: cores.cinza.escuro
                                                                }}
                                                            >
                                                                Aula 2
                                                            </Typography>
                                                        </Box>
                                                        <Chip
                                                            size="small"
                                                            label={`${frequenciaSalva.presencas.aula_2.filter(p => p).length}/4`}
                                                            color={frequenciaSalva.presencas.aula_2.filter(p => p).length >= 3 ? "success" : frequenciaSalva.presencas.aula_2.filter(p => p).length > 0 ? "warning" : "error"}
                                                            sx={{ height: 24, fontWeight: 600 }}
                                                        />
                                                    </Box>

                                                    <Box sx={{ p: 2 }}>
                                                        <Grid container spacing={1}>
                                                            {frequenciaSalva.presencas.aula_2.map((presente, aulaIndex) => (
                                                                <Grid item xs={6} key={aulaIndex}>
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            p: 1,
                                                                            borderRadius: 1,
                                                                            bgcolor: presente ? 'rgba(0, 200, 83, 0.08)' : 'transparent',
                                                                            border: presente ? '1px solid rgba(0, 200, 83, 0.2)' : '1px solid transparent'
                                                                        }}
                                                                    >
                                                                        {presente ?
                                                                            <CheckCircle fontSize="small" sx={{ color: cores.sucesso.principal, mr: 1 }} /> :
                                                                            <Close fontSize="small" sx={{ color: cores.erro.principal, mr: 1 }} />
                                                                        }
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontWeight: presente ? 600 : 400,
                                                                                color: presente ? cores.secundaria.principal : cores.cinza.escuro
                                                                            }}
                                                                        >
                                                                            Presença {aulaIndex + 1}
                                                                        </Typography>
                                                                    </Box>
                                                                </Grid>
                                                            ))}
                                                        </Grid>
                                                    </Box>
                                                </Paper>
                                            </Box>
                                        </Box>
                                    </Box>
                                )}                                <Box component={List} sx={{ mt: 2 }}>
                                    {minhaAvaliacao.map((item, index) => (
                                        <ListItem
                                            key={index}
                                            divider={index < minhaAvaliacao.length - 1}
                                            sx={{
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                alignItems: { xs: 'flex-start', sm: 'center' },
                                                py: 2,
                                                px: 2,
                                                borderColor: 'rgba(0,0,0,0.08)'
                                            }}
                                        >
                                            <ListItemAvatar sx={{ display: { xs: 'none', sm: 'flex' } }}>
                                                <Avatar
                                                    sx={{
                                                        bgcolor: 'rgba(30, 58, 138, 0.1)',
                                                        color: cores.primaria.principal
                                                    }}
                                                >
                                                    <Grading />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primaryTypographyProps={{ sx: { fontWeight: 600, color: cores.cinza.escuro } }}
                                                primary={`${item.criterio_descricao} (Peso: ${item.peso})`}
                                                secondary={
                                                    <React.Fragment>
                                                        <Typography
                                                            component="span"
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 'bold',
                                                                display: 'block',
                                                                mt: 1
                                                            }}
                                                            color="primary"
                                                        >
                                                            <Chip
                                                                label={`Nota: ${item.nota_recebida}`}
                                                                size="small"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.08)`,
                                                                    color: cores.primaria.principal,
                                                                    height: 24
                                                                }}
                                                            />
                                                        </Typography>
                                                        <Typography
                                                            component="span"
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{
                                                                display: 'block',
                                                                fontStyle: 'italic',
                                                                mt: 1,
                                                                px: 1,
                                                                py: 1,
                                                                borderLeft: `3px solid ${cores.cinza.claro}`,
                                                                bgcolor: 'rgba(241, 245, 249, 0.6)'
                                                            }}
                                                        >
                                                            {item.observacao_criterio || 'Sem observações.'}
                                                        </Typography>
                                                    </React.Fragment>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </Box>
                            </>
                        ) : (
                            <List sx={{ mt: 2 }}>
                                {templateAvaliacao?.criterios?.map((criterio, index) => (
                                    <ListItem
                                        key={criterio.id}
                                        divider={index < templateAvaliacao.criterios.length - 1}
                                        sx={{
                                            py: 1.5,
                                            px: 2,
                                            borderColor: 'rgba(0,0,0,0.08)'
                                        }}
                                    >
                                        <ListItemAvatar>
                                            <Avatar
                                                sx={{
                                                    bgcolor: 'rgba(30, 58, 138, 0.1)',
                                                    color: cores.primaria.principal
                                                }}
                                            >
                                                <Grading />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primaryTypographyProps={{ sx: { fontWeight: 600, color: cores.cinza.escuro } }}
                                            primary={criterio.descricao}
                                            secondary={
                                                <Box sx={{ mt: 0.5 }}>
                                                    <Chip
                                                        label={`Peso: ${criterio.peso}`}
                                                        size="small"
                                                        sx={{
                                                            fontWeight: 600,
                                                            bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.08)`,
                                                            color: cores.primaria.principal,
                                                            height: 24
                                                        }}
                                                    />
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Paper>
                </Fade>
            );
        }

        return null;
    };

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
            <Fade in={true} timeout={500}>
                <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, boxShadow: cores.sombras.medio }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 4,
                        p: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        background: cores.gradientes.primario,
                        color: 'white'
                    }}>
                        <IconButton
                            onClick={() => navegar(`/minha-turma/${turma.id}`)}
                            sx={{
                                mr: 2,
                                color: 'white',
                                bgcolor: 'rgba(255,255,255,0.2)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                }
                            }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                {caso.titulo}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    opacity: 0.9,
                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    mt: 0.5
                                }}
                            >
                                <ArticleIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                                Detalhes do caso clínico
                            </Typography>
                        </Box>
                    </Box>

                    <Fade in={true} timeout={400}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2, sm: 3 },
                                my: 2,
                                background: '#f8f9fa',
                                borderRadius: 2,
                                border: `1px solid ${cores.cinza.claro}`,
                                boxShadow: cores.sombras.suave
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar
                                    sx={{
                                        mr: 2,
                                        bgcolor: 'rgba(30, 58, 138, 0.1)',
                                        color: cores.primaria.principal
                                    }}
                                >
                                    <Assignment />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: cores.primaria.principal }}>
                                    Descrição do Caso
                                </Typography>
                            </Box>
                            <Typography
                                variant="body1"
                                sx={{
                                    mt: 1,
                                    pl: { xs: 0, sm: '56px' },
                                    whiteSpace: 'pre-wrap',
                                    overflowWrap: 'anywhere',
                                    wordBreak: 'break-word',
                                    color: cores.cinza.escuro,
                                    lineHeight: 1.6
                                }}
                            >
                                {caso.descricao}
                            </Typography>

                            {imagens && imagens.length > 0 && (
                                <Box sx={{ mt: 3, pl: { xs: 0, sm: '56px' } }}>
                                    <Divider sx={{ my: 2 }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <ImageIcon
                                            sx={{
                                                mr: 1,
                                                color: cores.secundaria.principal
                                            }}
                                        />
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                fontWeight: 600,
                                                color: cores.secundaria.principal
                                            }}
                                        >
                                            Imagens do Caso
                                        </Typography>
                                    </Box>

                                    <Grid container spacing={2}>
                                        {imagens.map((imagem) => (
                                            <Grid item xs={12} sm={6} md={4} key={imagem.id}>
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        borderRadius: 2,
                                                        overflow: 'hidden',
                                                        boxShadow: cores.sombras.suave,
                                                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                                        cursor: 'pointer',
                                                        height: 200,
                                                        '&:hover': {
                                                            transform: 'scale(1.02)',
                                                            boxShadow: cores.sombras.medio,
                                                            '& .zoom-icon': {
                                                                opacity: 1,
                                                            }
                                                        }
                                                    }}
                                                    onClick={() => ampliarImagem(imagem)}
                                                >
                                                    <img
                                                        src={imagem.imagem_url}
                                                        alt={`Imagem ${imagem.id} do caso clínico`}
                                                        style={{
                                                            height: '100%',
                                                            width: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                    <Box
                                                        className="zoom-icon"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            background: 'rgba(0,0,0,0.3)',
                                                            opacity: 0,
                                                            transition: 'opacity 0.3s ease'
                                                        }}
                                                    >
                                                        <ZoomInIcon sx={{ fontSize: 40, color: 'white' }} />
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}
                        </Paper>
                    </Fade>

                    {renderizarConteudoPorPerfil()}
                </Paper>
            </Fade>

            <Modal
                open={!!imagemAmpliada}
                onClose={fecharImagemAmpliada}
                aria-labelledby="modal-imagem-ampliada"
                aria-describedby="modal-imagem-do-caso-ampliada"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        outline: 'none',
                    }}
                    onClick={fecharImagemAmpliada}
                >
                    {imagemAmpliada && (
                        <img
                            src={imagemAmpliada.imagem_url}
                            alt="Imagem ampliada do caso clínico"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '90vh',
                                objectFit: 'contain',
                                borderRadius: 8,
                                boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
                            }}
                        />
                    )}
                    <IconButton
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': {
                                bgcolor: 'rgba(0,0,0,0.7)'
                            }
                        }}
                        onClick={fecharImagemAmpliada}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Modal>
        </Container>
    );
};

export default CasoDetalhePagina;